import { Box } from "@mui/material";
import { usePortfolioGroupVersionStatData } from "../../usePortfolioGroupVersionStatData";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { usePortfolioGroupInfo } from "../../usePortfolioGroupInfo";
import { ChartOutUnderPerformance } from "../ChartOutUnderPerformance";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";
import { useYearRangeSelector } from "./../useYearRangeSelector";
import { getYear, parseISO } from "date-fns";
import { YearRangeSelector } from "./../YearRangeSelector";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { AnalyzeModuleStyledPage } from "./AnalyzeModuleStyledPage";
import { useAnalyzeModuleChartDataSelector } from "./useAnalyzeModuleChartDataSelector";
import {
  assetAllocationPages,
  portfolioAnalysisPages,
} from "@app/const/route.const";
import { openInNewTab } from "@app/utils";
import { CarDownloadButton } from "@app/components/Buttons";
import { UsePortfolioGroupVersionDataParams } from "../../usePortfolioGroupVersionData";

interface AnalyzeModulePerformanceRiskProps {
  versionParams: UsePortfolioGroupVersionDataParams;
  moduleId: string;
  moduleIdx: number;
}

export const AnalyzeModulePerformanceRisk = (
  props: AnalyzeModulePerformanceRiskProps,
) => {
  const { portfolioGroupId } = usePortfolioGroupParams();

  const pageContent = usePageContent();

  const { moduleId, moduleIdx } = props;

  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const statData = usePortfolioGroupVersionStatData(props.versionParams);

  const years = Array.from(
    new Set(
      statData.oneYearUnderPerf.at(0)?.x.map((i) => getYear(parseISO(i))) ?? [],
    ),
  );

  const yearRangeSelector = useYearRangeSelector({
    years,
  });

  const defaultIds = statData.getDefaultDataIdsForModule(moduleId);

  const chartDataSelector = useAnalyzeModuleChartDataSelector({
    portfolioGroupVersionId: props.versionParams.versionId,
    ...defaultIds,
  });

  const handleShowPdf = () => {
    if (
      chartDataSelector.data1Id &&
      chartDataSelector.data2Id &&
      yearRangeSelector.startYear &&
      yearRangeSelector.endYear
    ) {
      const params = {
        portfolioGroupId,
        moduleId: props.moduleId,
        data1Id: chartDataSelector.data1Id,
        data2Id: chartDataSelector.data2Id,
        startYear: yearRangeSelector.startYear,
        endYear: yearRangeSelector.endYear,
      };

      openInNewTab(
        groupInfo.isSinglePortfolioMode
          ? portfolioAnalysisPages.getPdfPerformanceRiskReportUrl(params)
          : assetAllocationPages.getPdfModulePerformanceRiskReportUrl(params),
      );
    }
  };

  return (
    <AnalyzeModuleStyledPage>
      <TablePlaceHolder
        isLoading={
          groupInfo.isLoading || statData.isLoading || pageContent.isLoading
        }
        rows={5}
      >
        <CarPageContent
          isWhiteContext
          content={pageContent.getContent(
            groupInfo.isSinglePortfolioMode
              ? CarPageContentCode.PORTFOLIO_ANALYSIS_ANALYZE_PERFORMANCE_RISK
              : CarPageContentCode.ANALYZE_PORTFOLIO_GROUP_REPORTS_MODULE_PERFORMANCE_RISK,
          )}
        />
        <CarDownloadButton
          onClick={handleShowPdf}
          sx={{ mt: 2, mb: -2, zIndex: 1, ml: "auto", alignSelf: "flex-end" }}
        >
          Download PDF
        </CarDownloadButton>
        <YearRangeSelector
          sx={{ ml: 3 }}
          yearRangeSelector={yearRangeSelector}
        />
        <Box
          sx={{
            mt: 5,
            display: "flex",
            flexDirection: "column",
            gap: 5,
            px: 3,
          }}
        >
          <ChartOutUnderPerformance
            selectorLabel1="Portfolio Variation 1"
            selectorLabel2="Portfolio Variation 2"
            label="1 Year Under Performance Risk"
            data={statData.oneYearUnderPerf}
            periods={statData.periods}
            startYear={yearRangeSelector.startYear}
            endYear={yearRangeSelector.endYear}
            isRelative={false}
            chartDataSelector={chartDataSelector}
            pendoClass={pendoClasses.analyzePortfolioGroupReportsModuleOneYearUnderPerfChart(
              moduleIdx,
            )}
          />

          <ChartOutUnderPerformance
            selectorLabel1="Portfolio Variation 1"
            selectorLabel2="Portfolio Variation 2"
            label="3 Year Under Performance Risk"
            data={statData.threeYearUnderPerf}
            periods={statData.periods}
            startYear={yearRangeSelector.startYear}
            endYear={yearRangeSelector.endYear}
            isRelative={false}
            chartDataSelector={chartDataSelector}
            pendoClass={pendoClasses.analyzePortfolioGroupReportsModuleThreeYearUnderPerfChart(
              moduleIdx,
            )}
          />
          <ChartOutUnderPerformance
            selectorLabel1="Portfolio Variation 1"
            selectorLabel2="Portfolio Variation 2"
            label="Cumulative Relative Performance"
            data={statData.cumRelPerf}
            periods={statData.periods}
            chartDataSelector={chartDataSelector}
            startYear={yearRangeSelector.startYear}
            endYear={yearRangeSelector.endYear}
            isRelative={true}
            pendoClass={pendoClasses.analyzePortfolioGroupReportsModuleCumulativeRelativePerfChart(
              moduleIdx,
            )}
          />
        </Box>
      </TablePlaceHolder>
    </AnalyzeModuleStyledPage>
  );
};
